import { AsyncPipe, DecimalPipe } from '@angular/common'
import { ChangeDetectionStrategy, Component, Input, inject } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { MatBadge } from '@angular/material/badge'
import { MatIconButton } from '@angular/material/button'
import { MatCheckbox, MatCheckboxChange } from '@angular/material/checkbox'
import { MatError, MatFormField, MatHint, MatLabel } from '@angular/material/form-field'
import { MatIcon } from '@angular/material/icon'
import { MatInput } from '@angular/material/input'
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu'
import { MatTab, MatTabContent, MatTabGroup, MatTabLabel } from '@angular/material/tabs'
import { MatTooltip } from '@angular/material/tooltip'
import {
  ElderAuditedEntityComponent,
  ElderDialogService,
  ElderFormFieldLabelDirective,
  ElderQuantityInputControlComponent,
  ElderQuantityPipe,
  ElderQuantityRangeValidator,
  ElderRequiredQuantityValidator,
  ElderSelectComponent,
  ElderSinglePaneWrapperComponent,
  ElderTabGroupRoutingDirective,
  ElderToastService,
  ElderTranslatePipe,
  ElderTranslateService,
  IsoDurationPipe,
} from '@elderbyte/ngx-starter'
import { LoggerFactory } from '@elderbyte/ts-logger'
import { Duration, Period } from '@js-joda/core'
import { BehaviorSubject, Observable } from 'rxjs'
import { ProductSalesQuantityPipe } from '../../../../features/pipes/product-sales-quantity.pipe'
import { ProductSalesUnitWeightPipe } from '../../../../features/pipes/product-sales-weight.pipe'
import { OutputPreviewService } from '../../../output/preview/output-preview.service'
import { ReportSpec } from '../../../output/report/report-job'
import { ProcessLeadTimeSpecUtils, ProcessPlan } from '../../../process/process-plans/process-plan'
import { ProcessPlanSelectDirective } from '../../../process/process-plans/process-plan-select.directive'
import { Bom } from '../bom/bom'
import { BomEditorComponent } from '../bom/bom-editor/bom-editor.component'
import { MaterialComponent } from '../bom/material-component'
import { WorkItemCommand, WorkItemCommandType } from '../commands/work-item-command'
import { ItemProductionRouteDetailComponent } from '../item-production-route-detail/item-production-route-detail.component'
import { ItemWorkSegmentsComponent } from '../item-work-segments/item-work-segments.component'
import {
  WorkItemPack,
  WorkItemPackagesComponent,
} from '../packaging/work-item-packages/work-item-packages.component'
import {
  ValidationState,
  WorkItem,
  WorkItemState,
  WorkItemUpdateOne,
  WorkItemUtils,
} from '../work-item'
import { WorkItemFilter, WorkItemService } from '../work-item.service'
import {
  PackageDetailDialogComponent,
  IPackageDialogOptions,
} from '../../../packages/package-detail-dialog/package-detail-dialog.component'
import { MatDialog } from '@angular/material/dialog'
import { ItemPlannedSegmentsComponent } from '../item-planned-segments/item-planned-segments.component'

@Component({
  selector: 'app-work-item-content',
  templateUrl: './work-item-content.component.html',
  styleUrls: ['./work-item-content.component.scss'],
  imports: [
    MatIconButton,
    MatIcon,
    ElderTranslatePipe,
    ElderSinglePaneWrapperComponent,
    MatTabGroup,
    ElderTabGroupRoutingDirective,
    MatTab,
    FormsModule,
    MatTooltip,
    MatMenuTrigger,
    MatMenu,
    MatMenuItem,
    MatFormField,
    ElderFormFieldLabelDirective,
    MatLabel,
    MatInput,
    MatError,
    MatHint,
    ElderQuantityInputControlComponent,
    ElderRequiredQuantityValidator,
    ElderQuantityRangeValidator,
    ElderAuditedEntityComponent,
    ElderSelectComponent,
    ProcessPlanSelectDirective,
    MatCheckbox,
    WorkItemPackagesComponent,
    ItemProductionRouteDetailComponent,
    MatTabLabel,
    MatBadge,
    ItemWorkSegmentsComponent,
    MatTabContent,
    BomEditorComponent,
    AsyncPipe,
    DecimalPipe,
    IsoDurationPipe,
    ProductSalesQuantityPipe,
    ProductSalesUnitWeightPipe,
    ElderQuantityPipe,
    ItemPlannedSegmentsComponent,
  ],
})
export class WorkItemContentComponent {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  private readonly logger = LoggerFactory.getLogger(this.constructor.name)

  public readonly item$ = new BehaviorSubject<WorkItem>(null)
  public readonly processPlan$ = new BehaviorSubject<ProcessPlan>(null)

  public itemImage: string
  public readonly WorkItemState = WorkItemState
  public readonly ValidationState = ValidationState
  public readonly WorkItemUtils = WorkItemUtils
  private workItemService = inject(WorkItemService)
  private toastService = inject(ElderToastService)
  private dialogService = inject(ElderDialogService)
  private outputPreviewService = inject(OutputPreviewService)
  private translate = inject(ElderTranslateService)
  dialog = inject(MatDialog)

  /***************************************************************************
   *                                                                         *
   * Getters / Setters                                                       *
   *                                                                         *
   **************************************************************************/

  public get producedProduct(): MaterialComponent {
    return this.item.producedProduct
  }

  public get item(): WorkItem {
    return this.item$.getValue()
  }

  @Input({ required: true })
  public set item(item: WorkItem) {
    this.item$.next(item)
    if (item) {
      Bom.populateNamed(item.engineeringBom)
      Bom.populateNamed(item.consumptionBom)
      Bom.populateNamed(item.finishedBom)
      Bom.populateNamed(item.manufacturingBom)
      this.itemImage = this.workItemService.imageUrl(item)
    }
  }

  /***************************************************************************
   *                                                                         *
   * Public API                                                              *
   *                                                                         *
   **************************************************************************/

  public onPreviewItemLabel(_: Event): void {
    this.outputPreviewService.openPreviewDialog(ReportSpec.itemLabel(this.item.id))
  }

  public onPreviewPieceLabel(_: Event): void {
    this.outputPreviewService.openPreviewDialog(ReportSpec.itemPieceLabel(this.item.id))
  }

  public onPreviewLoadError(_: any): void {
    this.itemImage = null
  }

  public onSave(event?: Event): void {
    const workItemPatch = new WorkItemUpdateOne()
    workItemPatch.id = this.item.id
    workItemPatch.processPlanId = this.item.processPlanId
    workItemPatch.optimizeAssignment = this.item.optimizeAssignment

    this.patch(workItemPatch)
  }

  public onOptimizableChange($event: MatCheckboxChange): void {
    this.item.optimizeAssignment = $event.checked
  }

  public onItemChangedId(itemId: string): void {
    this.loadItem(itemId)
  }

  public getProcessValidationTooltip(item: WorkItem): string {
    if (!WorkItemUtils.segmentsAreValid(item)) {
      return this.translate.instant('manufacturing.title')
    } else if (WorkItemUtils.bomIsValid(item?.consumptionBom, item?.processingType)) {
      return this.translate.instant('bom.cBom')
    } else if (WorkItemUtils.bomIsValid(item?.finishedBom, item?.processingType)) {
      return this.translate.instant('bom.fBom')
    } else {
      return ''
    }
  }

  public isEmptyOrNull(parentBom: Bom): boolean {
    return !parentBom || parentBom?.namedItems?.length === 0
  }

  public calculateTotalIndependentLeadTime(item: WorkItem): Period {
    return ProcessLeadTimeSpecUtils.calculateTotalIndependentLeadTime(item.leadTimeSpec)
  }

  public calculateTotalDependentLeadTime(item: WorkItem): Duration {
    return ProcessLeadTimeSpecUtils.calculateTotalDependentLeadTime(item.leadTimeSpec)
  }

  public prepareItem(): void {
    this.itemCommandConfirmDialog().subscribe((result) => {
      if (result) {
        this.executeCommandType(WorkItemCommandType.PREPARE)
      }
    })
  }

  public calculateItemBoms(): void {
    this.itemCommandConfirmDialog().subscribe((result) => {
      if (result) {
        this.executeCommandType(WorkItemCommandType.CALCULATE_BOMS)
      }
    })
  }

  public cleanUpPackages(): void {
    this.executeCommandType(WorkItemCommandType.PACKAGE_CLEANUP)
  }

  public emit(): void {
    if (this.item.id) {
      this.workItemService.emit(WorkItemFilter.ids(this.item.id)).subscribe({
        next: (_) => this.toastService.info('messages.action_successful'),
        error: (err) => this.toastService.error('messages.action_failed', err),
      })
    }
  }

  public onProcessPlanChange(plan: ProcessPlan): void {
    this.processPlan$.next(plan)
  }

  packageClick(pack: WorkItemPack, company: string): void {
    if (pack) {
      this.dialog.open(PackageDetailDialogComponent, {
        maxWidth: '95vw',
        maxHeight: '95vw',
        width: '95vw',
        height: '95vh',
        data: <IPackageDialogOptions>{
          pkgId: pack.packageId,
          company,
        },
      })
    }
  }

  protected requestEnrichment(): void {
    this.executeCommand(new WorkItemCommand(this.item.id, WorkItemCommandType.ENRICH_SEGMENTS))
  }

  /***************************************************************************
   *                                                                         *
   * Private methods                                                         *
   *                                                                         *
   **************************************************************************/

  private itemCommandConfirmDialog(): Observable<boolean> {
    return this.dialogService.showConfirm({
      title: 'Befehl wirklich ausführen?',
      message:
        'Dieser Befehl kann Auswirkungen auf bereits abgeschlossene Positionen haben! ' +
        'Insbesondere Daten, die während der Produktion erstellt wurden könnten verloren gehen!',
      yesNo: true,
    })
  }

  private executeCommandType(type: WorkItemCommandType): void {
    this.executeCommand(new WorkItemCommand(this.item.id, type))
  }

  private executeCommand(cmd: WorkItemCommand): void {
    this.logger.debug('Executing command on item: ' + cmd.itemId, cmd)
    this.workItemService.executeCommand(cmd).subscribe({
      next: (result) => {
        if (result && result.success) {
          this.toastService.info('messages.action_successful')
          if (result.item) {
            this.item = result.item
          }
        } else {
          this.toastService.warn('messages.action_failed', { detail: result.message })
        }
      },
      error: (err) => this.toastService.error('messages.action_failed', err),
    })
  }

  private loadItem(itemId: string): void {
    if (itemId) {
      this.workItemService.findById(itemId).subscribe({
        next: (item) => {
          this.logger.debug('Successfully loaded item: ', item)
          this.item = item
        },
        error: (err) => this.logger.error('Failed to load Position ' + itemId, err),
      })
    }
  }

  private patch(patch: WorkItemUpdateOne): void {
    this.logger.debug('Patching item...', patch.id)

    this.workItemService.patch(patch).subscribe({
      next: (updated) => {
        this.item = updated
        this.toastService.info('messages.persisting_successful')
      },
      error: (err) => this.toastService.error('messages.persisting_failed', err),
    })
  }
}
